/**
 * SegmentTrack - Video track showing segment clips with thumbnails
 * Supports drag to reorder, trim handles, and selection
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { getEffectiveDuration, formatDuration } from '../utils/segmentUtils';

export function SegmentTrack({
  segments,
  pxPerMs,
  labelWidth,
  scrollLeft,
  selectedSegmentId,
  onSelectSegment,
  onUpdateSegment,
  onReorderSegments,
  getAssetUrl
}) {
  const [draggingId, setDraggingId] = useState(null);
  const [draggingType, setDraggingType] = useState(null); // 'move' | 'trim-start' | 'trim-end'
  const [dropIndex, setDropIndex] = useState(-1);
  const [thumbnails, setThumbnails] = useState({});
  const dragStartRef = useRef({ x: 0, startMs: 0, segmentData: null });
  const trackRef = useRef(null);

  // Generate thumbnails for segments
  useEffect(() => {
    const generateThumbnails = async () => {
      const newThumbnails = {};
      
      for (const segment of segments) {
        if (!segment.video_url || thumbnails[segment.id]) continue;
        
        try {
          const video = document.createElement('video');
          video.crossOrigin = 'anonymous';
          video.src = getAssetUrl(segment.video_url);
          video.currentTime = (segment.trim_start_ms || 0) / 1000 + 0.5;
          
          await new Promise((resolve, reject) => {
            video.onloadeddata = resolve;
            video.onerror = reject;
            setTimeout(reject, 5000);
          });

          const canvas = document.createElement('canvas');
          canvas.width = 160;
          canvas.height = 90;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          newThumbnails[segment.id] = canvas.toDataURL('image/jpeg', 0.7);
          
          video.src = '';
        } catch (e) {
          console.warn('Failed to generate thumbnail for segment', segment.id);
        }
      }
      
      if (Object.keys(newThumbnails).length > 0) {
        setThumbnails(prev => ({ ...prev, ...newThumbnails }));
      }
    };

    generateThumbnails();
  }, [segments, getAssetUrl]);

  // Handle mouse down on segment
  const handleMouseDown = useCallback((e, segment, type = 'move') => {
    e.preventDefault();
    e.stopPropagation();
    
    setDraggingId(segment.id);
    setDraggingType(type);
    dragStartRef.current = {
      x: e.clientX,
      startMs: segment.start_ms,
      trimStart: segment.trim_start_ms || 0,
      trimEnd: segment.trim_end_ms || 0,
      segmentData: { ...segment }
    };
  }, []);

  // Handle mouse move
  useEffect(() => {
    if (!draggingId) return;

    const handleMouseMove = (e) => {
      const deltaX = e.clientX - dragStartRef.current.x;
      const deltaMs = deltaX / pxPerMs;
      const segment = dragStartRef.current.segmentData;

      if (draggingType === 'move') {
        // Calculate drop position for reordering
        const track = trackRef.current;
        if (track) {
          const rect = track.getBoundingClientRect();
          const x = e.clientX - rect.left + scrollLeft;
          const timeMs = x / pxPerMs;
          
          // Find drop index
          let newDropIndex = 0;
          for (let i = 0; i < segments.length; i++) {
            const seg = segments[i];
            const segCenter = (seg.start_ms || 0) + getEffectiveDuration(seg) / 2;
            if (timeMs > segCenter) {
              newDropIndex = i + 1;
            }
          }
          setDropIndex(newDropIndex);
        }
      } else if (draggingType === 'trim-start') {
        // Trim from start
        const newTrimStart = Math.max(0, Math.min(
          dragStartRef.current.trimStart + deltaMs,
          (segment.duration_ms || 5000) - (segment.trim_end_ms || 0) - 500 // Min 500ms
        ));
        onUpdateSegment(segment.id, { trim_start_ms: Math.round(newTrimStart) });
      } else if (draggingType === 'trim-end') {
        // Trim from end
        const newTrimEnd = Math.max(0, Math.min(
          dragStartRef.current.trimEnd - deltaMs,
          (segment.duration_ms || 5000) - (segment.trim_start_ms || 0) - 500 // Min 500ms
        ));
        onUpdateSegment(segment.id, { trim_end_ms: Math.round(newTrimEnd) });
      }
    };

    const handleMouseUp = () => {
      if (draggingType === 'move' && dropIndex >= 0) {
        const currentIndex = segments.findIndex(s => s.id === draggingId);
        if (currentIndex !== -1 && currentIndex !== dropIndex && currentIndex !== dropIndex - 1) {
          onReorderSegments(currentIndex, dropIndex > currentIndex ? dropIndex - 1 : dropIndex);
        }
      }
      setDraggingId(null);
      setDraggingType(null);
      setDropIndex(-1);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingId, draggingType, dropIndex, segments, pxPerMs, scrollLeft, onUpdateSegment, onReorderSegments]);

  // Calculate total width
  const totalWidth = segments.reduce((sum, seg) => sum + getEffectiveDuration(seg), 0) * pxPerMs;

  return (
    <div className="h-24 bg-zinc-900/50 border-b border-zinc-800 flex">
      {/* Track label */}
      <div 
        className="shrink-0 border-r border-zinc-700 flex items-center justify-center bg-zinc-900 sticky left-0 z-10"
        style={{ width: labelWidth }}
      >
        <span className="text-xs text-zinc-400 font-medium">Video</span>
      </div>
      
      {/* Track content */}
      <div 
        ref={trackRef}
        className="relative flex-1 overflow-hidden"
      >
        <div 
          className="absolute top-0 h-full flex items-center py-1"
          style={{ width: Math.max(totalWidth, 1000), transform: `translateX(-${scrollLeft}px)` }}
        >
          {segments.map((segment, index) => {
            const effectiveDuration = getEffectiveDuration(segment);
            const width = effectiveDuration * pxPerMs;
            const left = (segment.start_ms || 0) * pxPerMs;
            const isSelected = selectedSegmentId === segment.id;
            const isDragging = draggingId === segment.id;
            
            return (
              <div
                key={segment.id}
                className={`absolute h-[88px] rounded-md overflow-hidden cursor-pointer transition-all ${
                  isSelected ? 'ring-2 ring-orange-500 ring-offset-1 ring-offset-zinc-900' : ''
                } ${isDragging ? 'opacity-50 scale-105' : 'hover:brightness-110'}`}
                style={{
                  left,
                  width: Math.max(width, 40),
                  backgroundColor: isSelected ? '#3f3f46' : '#27272a'
                }}
                onClick={() => onSelectSegment(segment.id)}
                onMouseDown={(e) => handleMouseDown(e, segment, 'move')}
              >
                {/* Thumbnail background */}
                {thumbnails[segment.id] && (
                  <div 
                    className="absolute inset-0 bg-cover bg-center opacity-60"
                    style={{ backgroundImage: `url(${thumbnails[segment.id]})` }}
                  />
                )}
                
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/40" />
                
                {/* Segment info */}
                <div className="absolute top-1 left-2 right-2 flex items-center justify-between">
                  <span className="text-[10px] font-medium text-white/90 bg-orange-500/80 px-1.5 py-0.5 rounded">
                    {index + 1}
                  </span>
                  {segment.speed && segment.speed !== 1 && (
                    <span className="text-[10px] text-white/80 bg-black/50 px-1 rounded">
                      {segment.speed}x
                    </span>
                  )}
                </div>
                
                {/* Duration label */}
                <div className="absolute bottom-1 left-2">
                  <span className="text-[10px] text-white/80">
                    {formatDuration(effectiveDuration)}
                  </span>
                </div>
                
                {/* Trim handles */}
                {isSelected && (
                  <>
                    {/* Left trim handle */}
                    <div
                      className="absolute left-0 top-0 bottom-0 w-2 bg-orange-500 cursor-ew-resize hover:bg-orange-400 flex items-center justify-center"
                      onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, segment, 'trim-start'); }}
                    >
                      <div className="w-0.5 h-6 bg-white/50 rounded" />
                    </div>
                    {/* Right trim handle */}
                    <div
                      className="absolute right-0 top-0 bottom-0 w-2 bg-orange-500 cursor-ew-resize hover:bg-orange-400 flex items-center justify-center"
                      onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, segment, 'trim-end'); }}
                    >
                      <div className="w-0.5 h-6 bg-white/50 rounded" />
                    </div>
                  </>
                )}
              </div>
            );
          })}
          
          {/* Drop indicator */}
          {dropIndex >= 0 && draggingType === 'move' && (
            <div
              className="absolute top-0 bottom-0 w-1 bg-orange-500 z-50"
              style={{
                left: dropIndex === 0 ? 0 : 
                  segments.slice(0, dropIndex).reduce((sum, s) => sum + getEffectiveDuration(s) * pxPerMs, 0)
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default SegmentTrack;
